import React, { useState } from 'react';
import { CircularProgress, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatterCurrency } from '../../../utils/methods';
import { COLUMN_FORMULAS } from './salesManagTableConfigs';

import {
  ProductTableScroll,
  ProductTableInner,
  PTRow,
  PTCell,
  PTGroupNameCell,
  PTExpandIcon,
  PTHeadLabel,
  PTMemberLink,
  PTStack,
  PTEmpty,
} from './SalesManagTab1Styled';

const num = value => formatterCurrency(value || 0);
const dash = value => (value ? formatterCurrency(value) : '-');
const refundText = value => (value ? `- ${formatterCurrency(value)}` : '-');
// 차감 항목(할인/미수금): 0이 아니면 '-' 부호를 붙이고, 0이면 그냥 0으로 표기.
const minusNum = value => (value ? `- ${formatterCurrency(Math.abs(value))}` : '0');
const countText = value => (Number(value) || 0).toLocaleString('ko-KR');

// 계산식 툴팁이 달린 금액 컬럼 헤더 (info 아이콘 inline).
const HeadWithInfo = ({ label }) => (
  <PTHeadLabel>
    {label}
    {COLUMN_FORMULAS[label] && (
      <Tooltip title={COLUMN_FORMULAS[label]} placement="top" arrow>
        <InfoOutlinedIcon sx={{ fontSize: 14, color: '#737373', ml: '4px', verticalAlign: 'middle' }} />
      </Tooltip>
    )}
  </PTHeadLabel>
);

// 그룹 합계는 서버 집계(group.*)를 1차로 사용하고, 없으면 detail 행 합으로 계산한다.
// (서버가 그룹당 count/regular/paid/discount/usedPoint/outstanding/cash/transfer/card/refund/total 을 내려준다.)
const sumRows = rows => {
  const acc = { count: 0, regular: 0, paid: 0, discount: 0, usedPoint: 0, outstanding: 0, cash: 0, transfer: 0, card: 0, refund: 0, total: 0 };
  (rows || []).forEach(r => {
    acc.count += 1;
    acc.regular += Number(r.regularPrice) || 0;
    acc.paid += Number(r.finalPrice) || 0;
    acc.discount += Number(r.discountPrice) || 0;
    acc.usedPoint += Number(r.usedPoint) || 0;
    acc.outstanding += Number(r.outstandingAmount) || 0;
    acc.cash += Number(r.cash) || 0;
    acc.transfer += Number(r.transfer) || 0;
    acc.card += Number(r.card) || 0;
    acc.refund += Number(r.refundAmount) || 0;
    acc.total += Number(r.finalPrice) || 0;
  });
  return acc;
};

// 그룹 객체에 서버 집계가 있으면 그대로 쓰고(권위 있는 값), 없으면 detail 합으로 보강한다.
const groupTotals = (group, rows) => {
  const hasServerAgg = group && group.total != null;
  if (hasServerAgg) {
    return {
      count: group.count,
      regular: group.regular,
      paid: group.paid,
      discount: group.discount,
      usedPoint: group.usedPoint,
      outstanding: group.outstanding,
      cash: group.cash,
      transfer: group.transfer,
      card: group.card,
      refund: group.refund,
      total: group.total,
    };
  }
  return sumRows(rows);
};

// 펼침 하위 테이블: 판매일/회원명/판매가격/결제금액/할인금액/미수금/현금/카드/환불금액/결제상태.
// 컬럼 너비는 상위 그룹 행과 동일하게 맞춰 시각적으로 연결한다.
// 마지막에 해당 그룹 소계 행을 굵게/배경으로 구분해 추가한다.
const DetailRows = ({ rows, loading, helpers, subtotal }) => {
  if (loading) {
    return (
      <PTRow variant="detail">
        <PTCell flex>
          <CircularProgress size={20} />
        </PTCell>
      </PTRow>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <PTRow variant="detail">
        <PTCell flex>개별 내역이 없습니다.</PTCell>
      </PTRow>
    );
  }

  return (
    <>
      <PTRow variant="detailHead">
        <PTCell cellWidth="200px">판매일</PTCell>
        <PTCell cellWidth="120px">회원명</PTCell>
        <PTCell cellWidth="140px"><HeadWithInfo label="판매가격" /></PTCell>
        <PTCell cellWidth="140px"><HeadWithInfo label="할인금액" /></PTCell>
        <PTCell cellWidth="140px"><HeadWithInfo label="포인트사용" /></PTCell>
        <PTCell cellWidth="140px"><HeadWithInfo label="미수금" /></PTCell>
        <PTCell cellWidth="140px"><HeadWithInfo label="결제금액" /></PTCell>
        <PTCell cellWidth="140px">현금</PTCell>
        <PTCell cellWidth="140px">계좌이체</PTCell>
        <PTCell cellWidth="140px">카드</PTCell>
        <PTCell cellWidth="140px"><HeadWithInfo label="환불금액" /></PTCell>
        <PTCell flex cellWidth="160px">결제상태</PTCell>
      </PTRow>

      {rows.map((row, index) => {
        const [date, time] = (row.paidDate || '').split(' ');
        return (
          <PTRow variant="detail" key={index}>
            <PTCell cellWidth="200px">
              <PTStack>
                <span>{date}</span>
                {time && <span>{time}</span>}
              </PTStack>
            </PTCell>
            <PTCell cellWidth="120px">
              <PTMemberLink
                clickable={!!row.memberId}
                onClick={() => row.memberId && helpers.onNavigateMember(row.memberId)}>
                {row.memberName || '(삭제회원)'}
              </PTMemberLink>
            </PTCell>
            <PTCell cellWidth="140px" align="right">{num(row.regularPrice)}</PTCell>
            <PTCell cellWidth="140px" align="right" tone={row.discountPrice ? 'minus' : undefined}>{minusNum(row.discountPrice)}</PTCell>
            <PTCell cellWidth="140px" align="right" tone={row.usedPoint ? 'minus' : undefined}>{minusNum(row.usedPoint)}</PTCell>
            <PTCell cellWidth="140px" align="right" tone={row.outstandingAmount ? 'minus' : undefined}>{minusNum(row.outstandingAmount)}</PTCell>
            <PTCell cellWidth="140px" align="right">{num(row.finalPrice)}</PTCell>
            <PTCell cellWidth="140px" align="right">{dash(row.cash)}</PTCell>
            <PTCell cellWidth="140px" align="right">{dash(row.transfer)}</PTCell>
            <PTCell cellWidth="140px" align="right">{dash(row.card)}</PTCell>
            <PTCell cellWidth="140px" align="right" tone={row.refundAmount ? 'refund' : undefined}>
              {refundText(row.refundAmount)}
            </PTCell>
            <PTCell flex cellWidth="160px" tone={row.rowType === 'REFUND' ? 'refund' : undefined}>
              {row.paymentStatusName}
            </PTCell>
          </PTRow>
        );
      })}

      {/* 그룹 소계 행 */}
      <PTRow variant="subtotal">
        <PTCell cellWidth="200px" align="left" bold>소계</PTCell>
        <PTCell cellWidth="120px" align="right" bold>{countText(subtotal.count)}</PTCell>
        <PTCell cellWidth="140px" align="right" bold>{num(subtotal.regular)}</PTCell>
        <PTCell cellWidth="140px" align="right" bold tone={subtotal.discount ? 'minus' : undefined}>{minusNum(subtotal.discount)}</PTCell>
        <PTCell cellWidth="140px" align="right" bold tone={subtotal.usedPoint ? 'minus' : undefined}>{minusNum(subtotal.usedPoint)}</PTCell>
        <PTCell cellWidth="140px" align="right" bold tone={subtotal.outstanding ? 'minus' : undefined}>{minusNum(subtotal.outstanding)}</PTCell>
        <PTCell cellWidth="140px" align="right" bold>{num(subtotal.paid)}</PTCell>
        <PTCell cellWidth="140px" align="right" bold>{dash(subtotal.cash)}</PTCell>
        <PTCell cellWidth="140px" align="right" bold>{dash(subtotal.transfer)}</PTCell>
        <PTCell cellWidth="140px" align="right" bold>{dash(subtotal.card)}</PTCell>
        <PTCell cellWidth="140px" align="right" bold tone={subtotal.refund ? 'refund' : undefined}>
          {refundText(subtotal.refund)}
        </PTCell>
        <PTCell flex cellWidth="160px" align="right" bold>{num(subtotal.total)}</PTCell>
      </PTRow>
    </>
  );
};

// 집계는 백엔드 /payments/summary 가 전체기간 기준으로 수행한다.
// 각 group 행: { groupKey, groupValue, count, regular, paid, discount, usedPoint, outstanding, cash, card, refund, total }.
// groupValue(라벨변환 전 raw 키)로 그룹 펼침 detail 을 lazy 조회한다.
const SalesManagGroupedTable = ({ groups = [], config, groupBy, onFetchDetail, helpers }) => {
  const { groupLabel } = config;

  // 전체 총계 = 모든 그룹 서버 집계의 합 (대시보드 합계와 일치).
  const grandTotal = React.useMemo(() => {
    const acc = { count: 0, regular: 0, paid: 0, discount: 0, usedPoint: 0, outstanding: 0, cash: 0, transfer: 0, card: 0, refund: 0, total: 0 };
    groups.forEach(g => {
      acc.count += Number(g.count) || 0;
      acc.regular += Number(g.regular) || 0;
      acc.paid += Number(g.paid) || 0;
      acc.discount += Number(g.discount) || 0;
      acc.usedPoint += Number(g.usedPoint) || 0;
      acc.outstanding += Number(g.outstanding) || 0;
      acc.cash += Number(g.cash) || 0;
      acc.transfer += Number(g.transfer) || 0;
      acc.card += Number(g.card) || 0;
      acc.refund += Number(g.refund) || 0;
      acc.total += Number(g.total) || 0;
    });
    return acc;
  }, [groups]);

  const [expandedKey, setExpandedKey] = useState(null); // 현재 펼친 groupValue
  const [detailCache, setDetailCache] = useState({}); // { groupValue: rows }
  const [loadingKey, setLoadingKey] = useState(null);

  // 탭/조회조건이 바뀌면 펼침 상태와 캐시를 초기화 (groupBy 기준)
  React.useEffect(() => {
    setExpandedKey(null);
    setDetailCache({});
    setLoadingKey(null);
  }, [groupBy, groups]);

  const handleToggle = async groupValue => {
    if (expandedKey === groupValue) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(groupValue);
    if (detailCache[groupValue] === undefined && onFetchDetail) {
      setLoadingKey(groupValue);
      try {
        const rows = await onFetchDetail(groupBy, groupValue);
        setDetailCache(prev => ({ ...prev, [groupValue]: rows }));
      } catch (e) {
        setDetailCache(prev => ({ ...prev, [groupValue]: [] }));
      } finally {
        setLoadingKey(null);
      }
    }
  };

  return (
    <ProductTableScroll>
      {/* 고정 칸 합 1,720px + 합계금액 최소 160px */}
      <ProductTableInner tableWidth="1880px">
        {/* 그룹 헤더 */}
        <PTRow variant="head">
          <PTCell cellWidth="200px">{groupLabel}</PTCell>
          <PTCell cellWidth="120px">결제건수</PTCell>
          <PTCell cellWidth="140px"><HeadWithInfo label="판매가격" /></PTCell>
          <PTCell cellWidth="140px"><HeadWithInfo label="할인금액" /></PTCell>
          <PTCell cellWidth="140px"><HeadWithInfo label="포인트사용" /></PTCell>
          <PTCell cellWidth="140px"><HeadWithInfo label="미수금" /></PTCell>
          <PTCell cellWidth="140px"><HeadWithInfo label="결제금액" /></PTCell>
          <PTCell cellWidth="140px">현금</PTCell>
          <PTCell cellWidth="140px">계좌이체</PTCell>
          <PTCell cellWidth="140px">카드</PTCell>
          <PTCell cellWidth="140px"><HeadWithInfo label="환불금액" /></PTCell>
          <PTCell flex cellWidth="160px"><HeadWithInfo label="합계금액" /></PTCell>
        </PTRow>

        {groups.length === 0 && <PTEmpty>데이터가 없습니다.</PTEmpty>}

        {groups.map(group => {
          const groupValue = group.groupValue ?? group.groupKey;
          const expanded = expandedKey === groupValue;
          return (
            <React.Fragment key={groupValue}>
              <PTRow variant="group" clickable onClick={() => handleToggle(groupValue)}>
                <PTGroupNameCell cellWidth="200px" align="left">
                  <span>{group.groupKey}</span>
                  <PTExpandIcon>
                    {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </PTExpandIcon>
                </PTGroupNameCell>
                <PTCell cellWidth="120px" align="right">{(group.count || 0).toLocaleString('ko-KR')}</PTCell>
                <PTCell cellWidth="140px" align="right">{num(group.regular)}</PTCell>
                <PTCell cellWidth="140px" align="right" tone={group.discount ? 'minus' : undefined}>{minusNum(group.discount)}</PTCell>
                <PTCell cellWidth="140px" align="right" tone={group.usedPoint ? 'minus' : undefined}>{minusNum(group.usedPoint)}</PTCell>
                <PTCell cellWidth="140px" align="right" tone={group.outstanding ? 'minus' : undefined}>{minusNum(group.outstanding)}</PTCell>
                <PTCell cellWidth="140px" align="right">{num(group.paid)}</PTCell>
                <PTCell cellWidth="140px" align="right">{dash(group.cash)}</PTCell>
                <PTCell cellWidth="140px" align="right">{dash(group.transfer)}</PTCell>
                <PTCell cellWidth="140px" align="right">{dash(group.card)}</PTCell>
                <PTCell cellWidth="140px" align="right" tone={group.refund ? 'refund' : undefined}>
                  {refundText(group.refund)}
                </PTCell>
                <PTCell flex cellWidth="160px" align="right" bold>{num(group.total)}</PTCell>
              </PTRow>

              {expanded && (
                <DetailRows
                  rows={detailCache[groupValue]}
                  loading={loadingKey === groupValue}
                  helpers={helpers}
                  subtotal={groupTotals(group, detailCache[groupValue])}
                />
              )}
            </React.Fragment>
          );
        })}

        {/* 전체 총계 행 */}
        {groups.length > 0 && (
          <PTRow variant="grandTotal">
            <PTCell cellWidth="200px" align="left" bold>총계</PTCell>
            <PTCell cellWidth="120px" align="right" bold>{countText(grandTotal.count)}</PTCell>
            <PTCell cellWidth="140px" align="right" bold>{num(grandTotal.regular)}</PTCell>
            <PTCell cellWidth="140px" align="right" bold tone={grandTotal.discount ? 'minus' : undefined}>{minusNum(grandTotal.discount)}</PTCell>
            <PTCell cellWidth="140px" align="right" bold tone={grandTotal.usedPoint ? 'minus' : undefined}>{minusNum(grandTotal.usedPoint)}</PTCell>
            <PTCell cellWidth="140px" align="right" bold tone={grandTotal.outstanding ? 'minus' : undefined}>{minusNum(grandTotal.outstanding)}</PTCell>
            <PTCell cellWidth="140px" align="right" bold>{num(grandTotal.paid)}</PTCell>
            <PTCell cellWidth="140px" align="right" bold>{dash(grandTotal.cash)}</PTCell>
            <PTCell cellWidth="140px" align="right" bold>{dash(grandTotal.transfer)}</PTCell>
            <PTCell cellWidth="140px" align="right" bold>{dash(grandTotal.card)}</PTCell>
            <PTCell cellWidth="140px" align="right" bold tone={grandTotal.refund ? 'refund' : undefined}>
              {refundText(grandTotal.refund)}
            </PTCell>
            <PTCell flex cellWidth="160px" align="right" bold>{num(grandTotal.total)}</PTCell>
          </PTRow>
        )}
      </ProductTableInner>
    </ProductTableScroll>
  );
};

export default SalesManagGroupedTable;
